/**
 * Pure waveform helpers for voice messages. No DOM/audio dependencies so they
 * are unit-testable in isolation.
 *
 * The Matrix voice-message spec (MSC1767 audio) expects a `waveform` array of
 * integer samples in the range 0..1024. `generateWaveform` downsamples raw
 * PCM-ish amplitude data into `buckets` normalized samples.
 */

export const WAVEFORM_MAX = 1024;

/**
 * Downsample an array of amplitudes (any positive scale) into `buckets`
 * integer samples in 0..WAVEFORM_MAX. Empty input yields a flat array.
 */
export function generateWaveform(amplitudes: readonly number[], buckets = 40): number[] {
  if (buckets <= 0) return [];
  if (amplitudes.length === 0) return new Array(buckets).fill(0);

  const bucketSize = amplitudes.length / buckets;
  const raw: number[] = [];
  for (let i = 0; i < buckets; i += 1) {
    const start = Math.floor(i * bucketSize);
    const end = Math.max(start + 1, Math.floor((i + 1) * bucketSize));
    let peak = 0;
    for (let j = start; j < end && j < amplitudes.length; j += 1) {
      const v = Math.abs(amplitudes[j]);
      if (v > peak) peak = v;
    }
    raw.push(peak);
  }

  const max = Math.max(...raw, 0);
  if (max === 0) return raw.map(() => 0);

  return raw.map((v) => Math.round((v / max) * WAVEFORM_MAX));
}

/**
 * Normalize a waveform (possibly on the 0..1024 scale, or arbitrary) into
 * 0..1 heights for rendering bars. Returns `buckets` values, resampling if
 * necessary.
 */
export function waveformToHeights(waveform: readonly number[], buckets = 40): number[] {
  if (buckets <= 0) return [];
  if (waveform.length === 0) return new Array(buckets).fill(0);

  const max = Math.max(...waveform, 0);
  const scale = max > 0 ? max : 1;

  const out: number[] = [];
  for (let i = 0; i < buckets; i += 1) {
    const idx = Math.min(waveform.length - 1, Math.floor((i / buckets) * waveform.length));
    out.push(clamp01(waveform[idx] / scale));
  }
  return out;
}

function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}
